import { getCountry } from '@iwIntlProvider/CountryProvider';
import { getDaysInMonth } from 'date-fns';
import _ from 'lodash';
import { defineMessages } from 'react-intl';

import { TSerialisedPeople } from './PersonModal.types';
import { getPeopleWithGuarantorStatus } from '../../../api/lendingAPI/CustomerStateAPIHelper';

const validationMessages = defineMessages({
    validNameRequired: {
        id: '_.person.error.valid_name_required',
        defaultMessage: 'Please enter a valid name'
    },
    validDateOfBirthRequired: {
        id: '_.person.error.valid_date_of_birth_required',
        defaultMessage: 'Please enter a valid date of birth'
    },
    emailAddressRequired: {
        id: '_.person.error.email_address_required',
        defaultMessage: 'Please enter an email address'
    },
    phoneNumberRequired: {
        id: '_.person.error.phone_number_required',
        defaultMessage: 'Please enter at least one phone number'
    },
    addressRequired: {
        id: '_.person.error.address_required',
        defaultMessage: 'Please enter at least one address'
    },
    postcodeRequired: {
        id: '_.person.error.postcode_required',
        defaultMessage: 'Please enter a postcode'
    },
    townRequired: {
        id: '_.person.error.town_required',
        defaultMessage: 'Please enter a town'
    },
    differentPhoneNumberRequired: {
        id: '_.person.error.different_phone_number_required',
        defaultMessage: 'Phone number cannot be the same as other guarantors'
    },
    differentEmailRequired: {
        id: '_.person.error.different_email_required',
        defaultMessage: 'Email cannot be the same as other guarantors'
    },
    consentForAdditionalGuarantorRequired: {
        id: '_.person.error.consent_for_additional_guarantor_required',
        defaultMessage: 'Consent required'
    }
});

export const validate = (people: TSerialisedPeople, values: any) => {
    let errors = {};

    if (!values.firstName) {
        errors = _.setWith(
            errors,
            'firstName',
            validationMessages.validNameRequired
        );
    }

    if (!values.lastName) {
        errors = _.setWith(
            errors,
            'lastName',
            validationMessages.validNameRequired
        );
    }

    const day = values?.dateOfBirth?.day;
    const month = values?.dateOfBirth?.month;
    const year = values?.dateOfBirth?.year;

    if (!day || isNaN(day)) {
        errors = _.setWith(
            errors,
            'dateOfBirth.day',
            validationMessages.validDateOfBirthRequired
        );
    }

    if (!month) {
        errors = _.setWith(
            errors,
            'dateOfBirth.month',
            validationMessages.validDateOfBirthRequired
        );
    }

    if (!year || year.length !== 4 || isNaN(year)) {
        errors = _.setWith(
            errors,
            'dateOfBirth.year',
            validationMessages.validDateOfBirthRequired
        );
    }

    if (
        day &&
        month &&
        year &&
        year.length === 4 &&
        // day out of range of month
        (day <= 0 || day > getDaysInMonth(new Date(`${year}-${month}-01`)))
    ) {
        errors = _.setWith(
            errors,
            'dateOfBirth.day',
            validationMessages.validDateOfBirthRequired
        );
    }

    const peopleWithGuarantorStatus = getPeopleWithGuarantorStatus(
        people,
        values.uid
    );
    if (!values.email) {
        errors = _.setWith(
            errors,
            'email',
            validationMessages.emailAddressRequired
        );
    }
    if (
        // https://emailregex.com/
        !new RegExp(
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ).test(values.email)
    ) {
        errors = _.setWith(
            errors,
            'email',
            validationMessages.emailAddressRequired
        );
    }
    if (values.email) {
        const emailFound = _.find(peopleWithGuarantorStatus.email, {
            email: values.email
        });
        if (emailFound) {
            errors = _.setWith(
                errors,
                'email',
                validationMessages.differentEmailRequired
            );
        }
    }
    if (!values.phones[0].number) {
        errors = _.setWith(
            errors,
            `phones[0].number`,
            validationMessages.phoneNumberRequired
        );
    }
    values.phones.forEach(
        (
            phone: { number: string; type: string; uid: string },
            index: number
        ) => {
            if (values.phones[index] && phone.number) {
                const numberFound = _.find(peopleWithGuarantorStatus.phones, {
                    number: phone.number
                });
                if (numberFound) {
                    errors = _.setWith(
                        errors,
                        `phones[${index}].number`,
                        validationMessages.differentPhoneNumberRequired
                    );
                }
            }
        }
    );

    if (!values.addresses || !values.addresses.length) {
        errors = _.setWith(
            errors,
            'addresses',
            validationMessages.addressRequired
        );
    }
    if (values.addresses) {
        const primaryAddress = values.addresses[0];

        if (primaryAddress && !primaryAddress.postcode) {
            errors = _.setWith(
                errors,
                `addresses[0].postcode`,
                validationMessages.postcodeRequired
            );
        }
        if (
            primaryAddress?.postcode &&
            getCountry() === 'DE' &&
            !new RegExp(/^\d{5}$/).test(primaryAddress.postcode)
        ) {
            errors = _.setWith(
                errors,
                `addresses[0].postcode`,
                validationMessages.postcodeRequired
            );
        }
        if (primaryAddress && !primaryAddress.town) {
            errors = _.setWith(
                errors,
                `addresses[0].town`,
                validationMessages.townRequired
            );
        }
    }

    if (
        values.isGuarantor === true &&
        values.additionalGuarantorConsent !== true
    ) {
        errors = _.setWith(
            errors,
            'additionalGuarantorConsent',
            validationMessages.consentForAdditionalGuarantorRequired
        );
    }

    return errors;
};
